// Collaborative editing adapter for README bodies.
//
// This is the seam between the Yjs CRDT engine and the CodeMirror editor. A
// "session" owns a single `Y.Doc` whose `Y.Text` named "markdown" holds the
// document body. CodeMirror binds to that `Y.Text` via `yCollab`, so local
// edits become CRDT updates and remote updates apply without clobbering the
// local caret/selection.
//
// Slice 1 (this file): LOCAL session only — no network provider. The Y.Doc is
// seeded from the README's current markdown and changes flow back out via
// `getText()` so the existing autosave can persist them. Awareness is created
// here (ephemeral, never persisted) so remote-cursor support can light up the
// moment a transport is attached.
//
// Slice 2+ will add a transport (Cloudflare Durable Object WebSocket provider)
// that syncs THIS SAME `ydoc` + `awareness`. The editor never needs to change:
// it already speaks Yjs. Keep the public shape stable so the provider can be
// layered on without touching `CollaborativeMarkdownEditor`.
import * as Y from "yjs";
import { Awareness } from "y-protocols/awareness";
import { yCollab } from "y-codemirror.next";
import YProvider from "y-partyserver/provider";
// The Y.Text field name. Stable across clients so everyone shares one rope.
export const MARKDOWN_FIELD = "markdown";
// Hostname of the collab Worker, injected at build time. When unset the editor
// stays LOCAL-ONLY (slice-1 behavior) even with the experimental flag on — a
// safe default that never breaks the app if the Worker isn't deployed.
export const COLLAB_HOST = import.meta.env.VITE_COLLAB_HOST || undefined;
// Must match the kebab-cased Durable Object binding name (`ReadmeRoom`) in
// collab-worker/wrangler.toml — partyserver routes `/parties/<party>/<room>`.
const COLLAB_PARTY = "readme-room";
export function collabRemoteEnabled() {
    return !!COLLAB_HOST;
}
function wsProtocol(host) {
    return /^(localhost|127\.|0\.0\.0\.0|\[::1\])/i.test(host) ? "ws" : "wss";
}
// Fetch a fresh collab token for `readmeId`. Called by the provider on every
// (re)connect via its async `params`, so reconnects always carry a valid token.
async function fetchCollabParams(readmeId) {
    try {
        const res = await fetch(`/api/readmes/${encodeURIComponent(readmeId)}/collab-token`, { credentials: "include" });
        if (!res.ok)
            return {};
        const body = (await res.json());
        return body.token ? { token: body.token } : {};
    }
    catch {
        return {};
    }
}
// Deterministic, pleasant cursor color derived from a stable string (user id
// or name) so the same collaborator keeps the same color across sessions.
const CURSOR_PALETTE = [
    "#2563eb", // blue
    "#059669", // emerald
    "#d97706", // amber
    "#dc2626", // red
    "#7c3aed", // violet
    "#0891b2", // cyan
    "#db2777", // pink
    "#65a30d", // lime
];
export function colorForKey(key) {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) & 0x7fffffff;
    }
    return CURSOR_PALETTE[hash % CURSOR_PALETTE.length];
}
/**
 * Create a collaborative session for a README body.
 *
 * - Remote (room set + `VITE_COLLAB_HOST` configured): attaches a y-partyserver
 *   `YProvider` to the room. The server seeds the document (via its `onLoad`),
 *   so we do NOT seed `initialText` locally — that would duplicate content once
 *   the server state syncs in.
 * - Local (otherwise): an in-memory Y.Doc seeded from `initialText`, no network.
 *   This is the slice-1 fallback and keeps the app working without the Worker.
 */
export function createReadmeCollabSession(opts) {
    const { initialText, user, room } = opts;
    const remote = !!room && collabRemoteEnabled();
    const ydoc = new Y.Doc();
    const ytext = ydoc.getText(MARKDOWN_FIELD);
    if (!remote && initialText) {
        // Local-only: seed from the README content. (Remote sessions are seeded by
        // the Worker's onLoad and synced down by the provider.)
        ydoc.transact(() => {
            ytext.insert(0, initialText);
        }, "seed");
    }
    const awareness = new Awareness(ydoc);
    awareness.setLocalStateField("user", {
        name: user.name,
        color: user.color,
        colorLight: user.color,
    });
    const extension = yCollab(ytext, awareness);
    let provider;
    if (remote && room && COLLAB_HOST) {
        provider = new YProvider(COLLAB_HOST, room, ydoc, {
            party: COLLAB_PARTY,
            awareness,
            protocol: wsProtocol(COLLAB_HOST),
            params: () => fetchCollabParams(room),
        });
    }
    return {
        ydoc,
        ytext,
        awareness,
        extension,
        isRemote: remote,
        getText: () => ytext.toString(),
        setText: (next) => {
            if (ytext.toString() === next)
                return;
            ydoc.transact(() => {
                ytext.delete(0, ytext.length);
                if (next)
                    ytext.insert(0, next);
            }, "external");
        },
        destroy: () => {
            // Tear down transport first, then awareness, then the doc.
            provider?.destroy();
            awareness.destroy();
            ydoc.destroy();
        },
    };
}
/**
 * Local-only session (no transport). Kept for callers/tests that want the
 * slice-1 behavior explicitly; delegates to {@link createReadmeCollabSession}.
 */
export function createLocalCollabSession(initialText, user) {
    return createReadmeCollabSession({ initialText, user });
}
